import { FC, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../../contexts/LanguageContext';

type PartnerLogo = {
  id: string;
  name: string;
  description: string;
  logo: string;
  website?: string;
};

const PartnersSection: FC = () => {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);

  // Partner logos data using actual logos from public/images/logos
  const partners: PartnerLogo[] = [
    {
      id: 'exci-maa-main',
      name: 'SRP ',
      description: 'Steuerberatungkammer Rhineland-Pfalz',
      logo: '/Logos/steuerberatungkammer-min.jpg',
      // website: 'https://www.exci-maa.com'
    },
    {
      id: 'exci-maa-main',
      name: 'INTEGRA INTERNATIONAL ',
      description: 'Integra International Asscociation',
      logo: '/Logos/blank-avatar-white.jpg',
      // website: 'https://www.exci-maa.com'
    },
    {
      id: 'exci-maa-alt',
      name: 'ICPAR',
      description: 'Institute of Certified Public Accountants of Rwanda',
      logo: '/Logos/ICPAR-Logo.jpg',
      //website: 'https://www.exci-maa.com'
    },
    {
      id: 'exci-maa-alt',
      name: 'NEC-CO',
      description: 'Onec Congo',
      logo: '/Logos/logo-pres.jpg',
      //website: 'https://www.exci-maa.com'
    },
    {
      id: 'exci-maa-placeholder',
      name: 'CPA CANADA',
      description: 'Comptable Professionnel Agréé Canada',
      logo: '/Logos/images.png',
      //website: 'https://www.exci-maa.com'
    },
    {
      id: 'onecca',
      name: 'ONECCA',
      description: 'ordre national des experts comptables et comptables agréés du Cameroon',
      logo: '/Logos/images (1).png',
      //website: 'https://www.exci-maa.com'
    },
    {
      id: 'Conta',
      name: 'ODEC',
      description: 'Ordre Des Experts Comptables',
      logo: '/Logos/Logo_de_l_ordre_des_experts_comptables.jpg',
      //website: 'https://www.exci-maa.com'
    },
    // {
    //   id: 'onec',
    //   name: 'ONEC',
    //   description: 'Ordre National des Experts Comptables du Gabon',
    //   logo: '/Logos/ONEC-LOGO-1.jph.webp',
    //   //website: 'https://www.exci-maa.com'
    // },
    {
      id: 'onecca',
      name: 'ONECCA',
      description: 'Ordre National des Experts Comptables du Cameroun',
      logo: '/Logos/download.png',
      //website: 'https://www.exci-maa.com'

    },
  ];

  // Auto-advance slideshow every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % partners.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [partners.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <section className="bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
            {t('aboutPage.partners.label', '')}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('aboutPage.partners.title', 'Our Partners & Affilliates')}
          </h2>
        </div>

        {/* Slideshow Container */}
        <div className="relative max-w-5xl mx-auto">
          <div className="overflow-hidden rounded-2xl bg-white shadow-lg">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, x: 300 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -300 }}
                transition={{ duration: 0.9, ease: "easeInOut" }}
                className="p-8 md:p-12"
              >
                <div className="flex flex-col md:flex-row items-center justify-center space-y-6 md:space-y-0 md:space-x-12">
                  {/* Logo */}
                  <div className="flex-shrink-0">
                    <div className="w-32 h-32 md:w-40 md:h-40 bg-gray-100 rounded-xl flex items-center justify-center shadow-sm">
                      <img
                        src={partners[currentSlide].logo}
                        alt={`${partners[currentSlide].name} logo`}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          // Fallback to text if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = `<div class="text-2xl font-bold text-gray-400">${partners[currentSlide].name}</div>`;
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="text-center md:text-left flex-1">
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                      {partners[currentSlide].name}
                    </h3>
                    <p className="text-lg text-gray-600 mb-4">
                      {partners[currentSlide].description}
                    </p>
                    {partners[currentSlide].website && (
                      <a
                        href={partners[currentSlide].website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium transition-colors"
                      >
                        Visiter le site
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Dots */}
          <div className="flex justify-center mt-8 space-x-2">
            {partners.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? 'bg-blue-600 scale-125'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Progress Bar */}
          <div className="mt-4 w-full bg-gray-200 rounded-full h-1">
            <motion.div
              className="bg-blue-600 h-1 rounded-full"
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: 5, ease: "linear" }}
              key={currentSlide}
            />
          </div>
        </div>

        {/* Additional Info */}
        <div className="text-center mt-12">
          <p className="text-sm text-gray-500">
            {t('aboutPage.partners.note', '')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default PartnersSection;
